import { ReactElement, useState } from 'react';

import Button from '~/ui/components/common/Button';
import Modal from '~/ui/components/common/Modal';
import CrisisListItemDetails from '~/ui/pages/Network/components/Crisis/CrisisListItemDetails';
import formatClientAddress from '~/utils/dataFormat/formatClientAddress';

import { useApiRequest } from '~/hooks/api';
import useTabVisibility from '~/hooks/browser/useTabVisibility';
import {
  IAllocationsDeletedEvent,
  IAllocationsUpdatedEvent,
  ICrisisCall,
  ICrisisCallIdEvent,
  ICrisisCallTeamMember,
  IVisitCanceledEvent,
  IVisitFinishedEvent,
  IVisitStartedEvent,
} from '~/services/api/crisis/types';
import { ALLOCATIONS_FILTER } from '~/store/crisis/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useCrisisSignalR, { CrisisSignalREvent } from '~/ui/hooks/useCrisisSignalR';

import styles from './SignalRCrisis.module.scss';

const SignalRCrisisAlert = (): ReactElement => {
  const [crisisCall, setCrisisCall] = useState<ICrisisCall | null>(null);
  const user = useStoreState(state => state.user.current);
  const clinicId = user?.clinic?.id;
  const crisisAccess = !!user?.crisisAccess;
  const { allocationsFilter } = useStoreState(state => state.crisis);

  const {
    addCrisisCallTeamMemberAllocations,
    addOrUpdateCrisisCall,
    finishCrisisVisit,
    onGetCrisisCalls,
    onGetCrisisCallTeamMembers,
    removeCrisisCall,
    removeCrisisCallTeamMemberAllocations,
    removeCrisisCallTeamMemberAllocationsByCrisisCall,
    startCrisisVisit,
    updateCrisisCallTeamMemberAllocations,
  } = useStoreActions(actions => actions.crisis);

  const { refetch: refetchCrisisCalls } = useApiRequest(
    () => onGetCrisisCalls({ clinicId: String(clinicId) }),
    {
      enabled: false,
    },
  );

  const { refetch: refetchGetCrisisCallTeamMembers } = useApiRequest(
    () =>
      onGetCrisisCallTeamMembers({
        clinicId: String(clinicId),
        onlyCrisis: allocationsFilter === ALLOCATIONS_FILTER.CRISIS_ONLY,
      }),
    {
      enabled: false,
    },
  );

  const refetchCrisisCallData = () => {
    refetchCrisisCalls();
    refetchGetCrisisCallTeamMembers();
  };

  const onCrisisCallReceived = (data: ICrisisCall) => {
    if (data.isNewCrisisCall !== false) {
      setCrisisCall(data);
    }

    addOrUpdateCrisisCall(data);
  };

  const getCrisisCallId = (data: ICrisisCallIdEvent): number | undefined => data.crisisCallId;

  const onAllocationsDeleted = (data: IAllocationsDeletedEvent) => {
    removeCrisisCallTeamMemberAllocations({
      allocationClientIds: data.allocationClientIds ?? [],
    });
  };

  const onCrisisCallResolved = (data: ICrisisCallIdEvent) => {
    const crisisCallId = getCrisisCallId(data);

    if (!crisisCallId) {
      return;
    }

    removeCrisisCall({ crisisCallId });
    removeCrisisCallTeamMemberAllocationsByCrisisCall({ crisisCallId });
  };

  const onCrisisCallDeleted = (data: ICrisisCallIdEvent) => {
    const crisisCallId = getCrisisCallId(data);

    if (!crisisCallId) {
      return;
    }

    removeCrisisCall({ crisisCallId });
  };

  const onVisitStarted = (data: IVisitStartedEvent) => {
    const { clientId, crisisCallId, startTimeUtc, userId } = data;

    if (crisisCallId ? !startTimeUtc : !userId) {
      return;
    }

    startCrisisVisit({
      userId,
      clientId,
      crisisCallId,
      startTimeUtc,
    });
  };

  const onVisitFinished = (data: IVisitFinishedEvent) => {
    const { clientId, crisisCallId, userId } = data;

    if (!crisisCallId && !userId) {
      return;
    }

    finishCrisisVisit({
      userId,
      clientId,
      crisisCallId,
      isCanceled: false,
    });
  };

  const onVisitCanceled = (data: IVisitCanceledEvent) => {
    const { clientId, crisisCallId, userId, otherVisitsExist } = data;

    if (crisisCallId ? otherVisitsExist && !userId : !userId) {
      return;
    }

    finishCrisisVisit({
      userId,
      clientId,
      crisisCallId,
      otherVisitsExist,
      isCanceled: true,
    });
  };

  const onAllocationsUpdated = (data: IAllocationsUpdatedEvent) => {
    updateCrisisCallTeamMemberAllocations({
      createdAllocations: data.createdAllocations ?? [],
      deletedClientAllocationsId: data.deletedClientAllocationsId ?? [],
    });
  };

  useCrisisSignalR<IAllocationsDeletedEvent>({
    action: CrisisSignalREvent.ALLOCATIONS_DELETED,
    callback: onAllocationsDeleted,
  });
  useCrisisSignalR<ICrisisCallIdEvent>({
    action: CrisisSignalREvent.CRISIS_CALL_RESOLVED,
    callback: onCrisisCallResolved,
  });
  useCrisisSignalR<IVisitStartedEvent>({
    action: CrisisSignalREvent.VISIT_STARTED,
    callback: onVisitStarted,
  });
  useCrisisSignalR<IVisitFinishedEvent>({
    action: CrisisSignalREvent.VISIT_FINISHED,
    callback: onVisitFinished,
  });
  useCrisisSignalR<IVisitCanceledEvent>({
    action: CrisisSignalREvent.VISIT_CANCELED,
    callback: onVisitCanceled,
  });
  useCrisisSignalR<ICrisisCallTeamMember[]>({
    action: CrisisSignalREvent.ALLOCATIONS_CREATED,
    callback: addCrisisCallTeamMemberAllocations,
  });
  useCrisisSignalR<IAllocationsUpdatedEvent>({
    action: CrisisSignalREvent.ALLOCATIONS_UPDATED,
    callback: onAllocationsUpdated,
  });
  useCrisisSignalR<ICrisisCall>({
    action: CrisisSignalREvent.CRISIS_CALL_RECEIVED,
    callback: onCrisisCallReceived,
  });
  useCrisisSignalR<ICrisisCall>({
    action: CrisisSignalREvent.CRISIS_CALL_UPDATED,
    callback: addOrUpdateCrisisCall,
  });
  useCrisisSignalR<ICrisisCallIdEvent>({
    action: CrisisSignalREvent.CRISIS_CALL_DELETED,
    callback: onCrisisCallDeleted,
  });

  useTabVisibility(clinicId && crisisAccess ? refetchCrisisCallData : undefined);

  const resetCrisisCall = () => {
    setCrisisCall(null);
  };

  if (!crisisCall) {
    return null;
  }

  return (
    <Modal
      open={!!crisisCall}
      className={styles.modal}
      onClose={resetCrisisCall}
      maxWidth="sm"
      fullWidth
    >
      <>
        <div className={styles.modalTitle}>New Crisis Client</div>
        <div className={styles.modalWrapper}>
          <CrisisListItemDetails
            photo={crisisCall.client.photo}
            name={`${crisisCall.client.firstName} ${crisisCall.client.lastName}`}
            emrMrn={crisisCall.client.emrMrn}
            location={formatClientAddress(crisisCall.address)}
            timeCall={crisisCall.receivedOn}
            coordinates={{ latitude: crisisCall.address?.lat, longitude: crisisCall.address?.lng }}
          />
        </div>
        <div className={styles.actionButtons}>
          <Button color="primary" variant="contained" onClick={resetCrisisCall}>
            Close
          </Button>
        </div>
      </>
    </Modal>
  );
};

export default SignalRCrisisAlert;
